import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    CheckCircle2,
    Copy,
    Pencil,
    XCircle,
    ShieldCheck,
    Inbox,
    AlertTriangle,
    Save,
    X as XIcon,
    Send,
    Mail,
} from "lucide-react";
import {
    listApprovals,
    listCampaigns,
    approveProspect,
    editProspectDraft,
    rejectProspect,
    markProspectManuallySent,
} from "@/lib/chiroApi";
import ScorePill from "@/components/ScorePill";

const TABS = [
    { key: "pending", label: "Pending review" },
    { key: "approved", label: "Approved" },
    { key: "rejected", label: "Rejected" },
    { key: "manually_sent", label: "Manually sent" },
];

export default function Approvals() {
    const [data, setData] = useState(null);
    const [campaigns, setCampaigns] = useState([]);
    const [tab, setTab] = useState("pending");
    const [loading, setLoading] = useState(true);

    const load = async () => {
        const [d, c] = await Promise.all([listApprovals(), listCampaigns()]);
        setData(d);
        setCampaigns(c);
        setLoading(false);
    };

    useEffect(() => {
        load();
    }, []);

    const cmpName = (id) => campaigns.find((c) => c.id === id)?.name || "—";

    const rows = useMemo(() => (data ? data[tab] || [] : []), [data, tab]);
    const counts = data ? data.counts : { pending: 0, approved: 0, rejected: 0, manually_sent: 0 };

    return (
        <div data-testid="approvals-page" className="max-w-5xl">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7cc93c] mb-2">
                // approvals · human-in-the-loop outreach review
            </div>
            <h1 className="text-3xl font-bold tracking-tight mb-2">Approvals</h1>
            <p className="text-[#9ca3af] text-sm mb-6 max-w-2xl">
                Safety-reviewed drafts queue here. Approve copies the subject + body to your
                clipboard for manual paste into Gmail. ProofCommand never sends outreach
                automatically. "Sent" is only true when you explicitly mark it after sending
                outside the app.
            </p>

            <div
                className="mb-6 p-3 border border-[#ff9f0a]/40 bg-[#ff9f0a]/5 rounded-[2px] flex items-start gap-2 text-[12px]"
                data-testid="approvals-no-autosend-banner"
            >
                <ShieldCheck className="w-4 h-4 text-[#ff9f0a] shrink-0 mt-0.5" />
                <div className="text-[#d1d5db]">
                    <strong className="text-[#ff9f0a]">Auto-send: disabled.</strong> No mailbox
                    is connected to ProofCommand. Approving copies text only.
                </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-4" data-testid="approvals-tabs">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        onClick={() => setTab(t.key)}
                        data-testid={`approvals-tab-${t.key}`}
                        className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-[2px] border transition-colors flex items-center gap-1.5 ${
                            tab === t.key
                                ? "border-[#7cc93c] text-[#7cc93c] bg-[#7cc93c]/5"
                                : "border-[#262a2e] text-[#9ca3af] hover:text-[#f3f4f6]"
                        }`}
                    >
                        {t.label}
                        <span
                            className={`text-[9px] px-1 rounded-[2px] ${
                                tab === t.key
                                    ? "bg-[#7cc93c]/15 text-[#7cc93c]"
                                    : "bg-[#1a1c1f] text-[#9ca3af]"
                            }`}
                        >
                            {counts[t.key] ?? 0}
                        </span>
                    </button>
                ))}
            </div>

            {loading && <div className="font-mono text-[12px] text-[#9ca3af]">Loading…</div>}

            {!loading && rows.length === 0 && (
                <EmptyTab tab={tab} />
            )}

            {!loading && rows.length > 0 && (
                <div className="space-y-3" data-testid="approvals-list">
                    {rows.map((p) => (
                        <ApprovalCard
                            key={p.id}
                            prospect={p}
                            campaignName={cmpName(p.campaign_id)}
                            onChanged={load}
                            tab={tab}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function EmptyTab({ tab }) {
    const messages = {
        pending: "No safety-reviewed drafts waiting on you. Generate drafts from a prospect's proof notes.",
        approved: "No approved drafts yet. Approve one from the pending tab to see it here.",
        rejected: "No rejected drafts.",
        manually_sent:
            "Nothing marked manually-sent yet. After approving and pasting into Gmail, click 'Mark as manually sent' on the approved tab.",
    };
    return (
        <div
            className="p-8 border border-dashed border-[#262a2e] text-center text-[#9ca3af] font-mono text-[12px]"
            data-testid={`approvals-empty-${tab}`}
        >
            <Inbox className="w-5 h-5 inline-block mr-2 -mt-1 opacity-60" />
            {messages[tab]}
        </div>
    );
}

function ApprovalCard({ prospect, campaignName, onChanged, tab }) {
    const initialSubject =
        prospect.outreach_subject ||
        (prospect.draft_email && prospect.draft_email.match(/^subject:\s*(.+)$/im)?.[1]) ||
        "";
    const initialBody =
        prospect.outreach_body ||
        (prospect.draft_email
            ? prospect.draft_email.replace(/^subject:.*\n?/i, "").trimStart()
            : "");

    const [editing, setEditing] = useState(false);
    const [subject, setSubject] = useState(initialSubject);
    const [body, setBody] = useState(initialBody);
    const [showFallback, setShowFallback] = useState(false);
    const [busy, setBusy] = useState(false);
    const [showRejectInput, setShowRejectInput] = useState(false);
    const [rejectReason, setRejectReason] = useState("");

    const blocked =
        prospect.disqualified ||
        (prospect.google_business_status &&
            prospect.google_business_status !== "OPERATIONAL");

    // iter25 — subject is mandatory before approval. Approve & Copy is gated
    // on this and the backend enforces it too. We never auto-fabricate.
    const subjectMissing = !(subject || "").trim();

    const copyText = `Subject: ${subject}\n\n${body}`;

    const tryClipboard = async () => {
        try {
            await navigator.clipboard.writeText(copyText);
            return true;
        } catch {
            return false;
        }
    };

    const handleApprove = async () => {
        if (blocked) {
            toast.error("Cannot approve a disqualified or non-operational prospect");
            return;
        }
        if (subjectMissing) {
            // iter25 — never silently fabricate a subject.
            toast.error("Subject required before approval.");
            return;
        }
        setBusy(true);
        // iter25 — if the human added/edited the subject inline (without
        // entering Edit mode), persist the change as a draft_edited receipt
        // FIRST so the audit trail captures the human edit before approval.
        const inlineSubjectChanged =
            !editing && (subject !== initialSubject || body !== initialBody);
        if (inlineSubjectChanged) {
            try {
                await editProspectDraft(prospect.id, { subject, body });
            } catch {
                // Non-fatal — the approval call below will still validate
                // the subject and either succeed or surface the same error.
            }
        }
        const copied = await tryClipboard();
        try {
            await approveProspect(prospect.id, {
                subject,
                body,
                copied_to_clipboard: copied,
            });
            if (copied) {
                toast.success("Approved + copied to clipboard. Paste into Gmail to send.");
                setShowFallback(false);
                // Immediate refresh — no setTimeout race. The card moves from
                // 'pending' to the Approved tab; user can switch to Approved
                // tab to find the 'Mark as manually sent' button.
                await onChanged();
            } else {
                toast.warning(
                    "Approved, but clipboard copy failed. Manual copy text is shown below.",
                );
                setShowFallback(true);
                // Fallback path: keep the textarea visible long enough to copy
                // by hand, then refresh so the card moves out of pending.
                setTimeout(() => {
                    onChanged();
                }, 8000);
            }
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail?.message) toast.error(detail.message);
            else toast.error("Approval failed");
        } finally {
            setBusy(false);
        }
    };

    const handleSaveEdit = async () => {
        setBusy(true);
        try {
            await editProspectDraft(prospect.id, { subject, body });
            toast.success("Draft saved");
            setEditing(false);
            onChanged();
        } catch {
            toast.error("Save failed");
        } finally {
            setBusy(false);
        }
    };

    const handleReject = async () => {
        setBusy(true);
        try {
            await rejectProspect(prospect.id, { reason: rejectReason });
            toast.success("Rejected");
            setShowRejectInput(false);
            setRejectReason("");
            onChanged();
        } catch {
            toast.error("Reject failed");
        } finally {
            setBusy(false);
        }
    };

    const handleManualSent = async () => {
        if (
            !confirm(
                "Mark this draft as manually sent?\n\nThis only sets a status. ProofCommand does not send the email.",
            )
        )
            return;
        setBusy(true);
        try {
            await markProspectManuallySent(prospect.id);
            toast.success("Marked as manually sent");
            onChanged();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail?.message) toast.error(detail.message);
            else toast.error("Mark failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="border border-[#262a2e] bg-[#0f1113] rounded-[2px] p-4"
            data-testid={`approval-card-${prospect.id}`}
        >
            {/* Header */}
            <div className="flex items-start gap-3 flex-wrap mb-3">
                <div className="flex-1 min-w-0">
                    <div className="font-bold text-[14px] truncate flex items-center gap-2">
                        {prospect.practice_name}
                        {prospect.disqualified && (
                            <span
                                className="font-mono text-[8px] uppercase tracking-[0.2em] text-[#ff453a] border border-[#ff453a]/40 px-1 py-[1px] rounded-[2px]"
                                data-testid={`approval-disqualified-${prospect.id}`}
                            >
                                DISQUALIFIED
                            </span>
                        )}
                        {prospect.google_business_status &&
                            prospect.google_business_status !== "OPERATIONAL" && (
                                <span
                                    className="font-mono text-[8px] uppercase tracking-[0.2em] text-[#ff453a] border border-[#ff453a]/40 px-1 py-[1px] rounded-[2px]"
                                    data-testid={`approval-not-operational-${prospect.id}`}
                                >
                                    {prospect.google_business_status}
                                </span>
                            )}
                    </div>
                    <div className="font-mono text-[10px] text-[#9ca3af] mt-0.5">
                        {campaignName} · safety-reviewed{" "}
                        {prospect.safety_reviewed_at
                            ? new Date(prospect.safety_reviewed_at).toLocaleString()
                            : "—"}
                    </div>
                </div>
                <ScorePill label="Fit" value={prospect.fit_score} />
                <ScorePill label="Opp" value={prospect.opportunity_score} />
                <span
                    className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#7cc93c] border border-[#7cc93c]/40 px-2 py-[2px] rounded-[2px] flex items-center gap-1"
                    data-testid={`approval-safety-pill-${prospect.id}`}
                >
                    <ShieldCheck className="w-3 h-3" />
                    SAFETY REVIEWED
                </span>
            </div>

            {prospect.top_observation && (
                <div
                    className="mb-3 border border-[#7cc93c]/30 bg-[#7cc93c]/5 p-2 rounded-[2px]"
                    data-testid={`approval-top-obs-${prospect.id}`}
                >
                    <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#7cc93c] mb-1">
                        Top observation used
                    </div>
                    <div className="text-[12px] text-[#f3f4f6]">{prospect.top_observation}</div>
                </div>
            )}

            {/* Subject + body preview / edit */}
            <div className="space-y-2">
                <div>
                    <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-[#9ca3af] mb-1">
                        Subject{tab === "pending" && <span className="text-[#ff9f0a]"> *</span>}
                    </label>
                    {editing || tab === "pending" ? (
                        <input
                            type="text"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            data-testid={`approval-subject-input-${prospect.id}`}
                            placeholder={
                                tab === "pending" && subjectMissing
                                    ? "Subject required before approval"
                                    : ""
                            }
                            className={`w-full bg-[#0a0b0d] border rounded-[2px] px-3 py-2 font-mono text-[12px] text-[#f3f4f6] focus:outline-none ${
                                tab === "pending" && subjectMissing
                                    ? "border-[#ff9f0a]/60 focus:border-[#ff9f0a]"
                                    : "border-[#262a2e] focus:border-[#7cc93c]"
                            }`}
                        />
                    ) : (
                        <div
                            className="bg-[#0a0b0d] border border-[#262a2e] rounded-[2px] px-3 py-2 font-mono text-[12px] text-[#f3f4f6] truncate"
                            data-testid={`approval-subject-${prospect.id}`}
                        >
                            {subject || <span className="text-[#6b7280]">— no subject —</span>}
                        </div>
                    )}
                    {tab === "pending" && subjectMissing && (
                        <div
                            className="font-mono text-[11px] text-[#ff9f0a] mt-1 flex items-center gap-1"
                            data-testid={`approval-subject-required-msg-${prospect.id}`}
                        >
                            <AlertTriangle className="w-3 h-3" />
                            Subject required before approval.
                        </div>
                    )}
                </div>
                <div>
                    <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-[#9ca3af] mb-1">
                        Body {!editing && "preview"}
                    </label>
                    {editing ? (
                        <textarea
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            rows={8}
                            data-testid={`approval-body-input-${prospect.id}`}
                            className="w-full bg-[#0a0b0d] border border-[#262a2e] rounded-[2px] px-3 py-2 font-mono text-[12px] text-[#f3f4f6] focus:outline-none focus:border-[#7cc93c]"
                        />
                    ) : (
                        <pre
                            className="bg-[#0a0b0d] border border-[#262a2e] rounded-[2px] px-3 py-2 font-mono text-[11px] text-[#d1d5db] whitespace-pre-wrap break-words max-h-40 overflow-auto"
                            data-testid={`approval-body-${prospect.id}`}
                        >
                            {body || <span className="text-[#6b7280]">— empty —</span>}
                        </pre>
                    )}
                </div>
            </div>

            {/* Fallback copy area when clipboard write fails */}
            {showFallback && (
                <div
                    className="mt-3 border border-[#ff9f0a]/40 bg-[#ff9f0a]/5 p-3 rounded-[2px]"
                    data-testid={`approval-fallback-${prospect.id}`}
                >
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff9f0a] mb-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Clipboard copy failed — manual copy
                    </div>
                    <textarea
                        readOnly
                        rows={6}
                        value={copyText}
                        data-testid={`approval-fallback-text-${prospect.id}`}
                        className="w-full bg-[#0a0b0d] border border-[#262a2e] rounded-[2px] px-3 py-2 font-mono text-[11px] text-[#f3f4f6]"
                    />
                </div>
            )}

            {/* Reject reason input */}
            {showRejectInput && (
                <div className="mt-3" data-testid={`approval-reject-input-row-${prospect.id}`}>
                    <input
                        type="text"
                        placeholder="Optional reason for rejection"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        data-testid={`approval-reject-reason-${prospect.id}`}
                        className="w-full bg-[#0a0b0d] border border-[#262a2e] rounded-[2px] px-3 py-2 font-mono text-[12px] text-[#f3f4f6]"
                    />
                </div>
            )}

            {/* Status footer */}
            {prospect.outreach_rejected_reason && tab === "rejected" && (
                <div className="mt-3 font-mono text-[11px] text-[#ff453a]">
                    Reason: {prospect.outreach_rejected_reason}
                </div>
            )}
            {prospect.outreach_manually_sent_at && tab === "manually_sent" && (
                <div className="mt-3 font-mono text-[11px] text-[#9ca3af]">
                    Manually sent {new Date(prospect.outreach_manually_sent_at).toLocaleString()}{" "}
                    · operator confirmation only · no mailbox send inside ProofCommand
                </div>
            )}
            {prospect.outreach_approved_at && tab === "approved" && (
                <div className="mt-3 font-mono text-[11px] text-[#9ca3af]">
                    Approved {new Date(prospect.outreach_approved_at).toLocaleString()}
                </div>
            )}

            {/* Action bar */}
            <div className="mt-4 flex flex-wrap gap-2">
                {tab === "pending" && (
                    <>
                        {editing ? (
                            <>
                                <button
                                    onClick={handleSaveEdit}
                                    disabled={busy}
                                    data-testid={`approval-save-${prospect.id}`}
                                    className="flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.15em] px-3 py-2 bg-[#7cc93c] text-[#090a0b] hover:bg-[#9be05c] disabled:opacity-50 rounded-[2px]"
                                >
                                    <Save className="w-3 h-3" />
                                    Save edits
                                </button>
                                <button
                                    onClick={() => {
                                        setEditing(false);
                                        setSubject(initialSubject);
                                        setBody(initialBody);
                                    }}
                                    data-testid={`approval-cancel-edit-${prospect.id}`}
                                    className="flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.15em] px-3 py-2 border border-[#262a2e] text-[#9ca3af] hover:text-[#f3f4f6] rounded-[2px]"
                                >
                                    <XIcon className="w-3 h-3" />
                                    Cancel
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    onClick={handleApprove}
                                    disabled={busy || blocked || subjectMissing}
                                    data-testid={`approval-approve-copy-${prospect.id}`}
                                    title={
                                        blocked
                                            ? "Disqualified or non-operational — cannot approve"
                                            : subjectMissing
                                              ? "Subject required before approval."
                                              : ""
                                    }
                                    className="flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.15em] px-3 py-2 bg-[#7cc93c] text-[#090a0b] hover:bg-[#9be05c] disabled:opacity-40 disabled:cursor-not-allowed rounded-[2px]"
                                >
                                    <CheckCircle2 className="w-3 h-3" />
                                    <Copy className="w-3 h-3" />
                                    Approve & Copy
                                </button>
                                <button
                                    onClick={() => setEditing(true)}
                                    disabled={busy || blocked}
                                    data-testid={`approval-edit-${prospect.id}`}
                                    className="flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.15em] px-3 py-2 border border-[#262a2e] text-[#9ca3af] hover:text-[#f3f4f6] hover:border-[#7cc93c] disabled:opacity-40 rounded-[2px]"
                                >
                                    <Pencil className="w-3 h-3" />
                                    Edit
                                </button>
                                {showRejectInput ? (
                                    <button
                                        onClick={handleReject}
                                        disabled={busy}
                                        data-testid={`approval-confirm-reject-${prospect.id}`}
                                        className="flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.15em] px-3 py-2 bg-[#ff453a] text-[#090a0b] hover:bg-[#ff6b62] disabled:opacity-50 rounded-[2px]"
                                    >
                                        <XCircle className="w-3 h-3" />
                                        Confirm reject
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => setShowRejectInput(true)}
                                        disabled={busy}
                                        data-testid={`approval-reject-${prospect.id}`}
                                        className="flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.15em] px-3 py-2 border border-[#ff453a]/40 text-[#ff453a] hover:bg-[#ff453a]/10 disabled:opacity-50 rounded-[2px] ml-auto"
                                    >
                                        <XCircle className="w-3 h-3" />
                                        Reject
                                    </button>
                                )}
                            </>
                        )}
                    </>
                )}

                {tab === "approved" && (
                    <>
                        <ComposeMailtoButton
                            email={prospect.email}
                            subject={subject}
                            body={body}
                            prospectId={prospect.id}
                        />
                        <button
                            onClick={handleManualSent}
                            disabled={busy}
                            data-testid={`approval-mark-sent-${prospect.id}`}
                            className="flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.15em] px-3 py-2 border border-[#7cc93c]/40 text-[#7cc93c] hover:bg-[#7cc93c]/10 disabled:opacity-50 rounded-[2px]"
                        >
                            <Send className="w-3 h-3" />
                            Mark as manually sent
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}


function ComposeMailtoButton({ email, subject, body, prospectId }) {
    const [showFallback, setShowFallback] = useState(false);
    const hasEmail = !!(email && email.trim());
    const copyText = `Subject: ${subject}\n\n${body}`;

    const handleClick = () => {
        if (!hasEmail) return;
        // Build mailto: URL. Subject + body get percent-encoded; long bodies
        // can exceed some clients' URL-length limits, so we offer a copy
        // fallback right next to the button if anything goes wrong.
        const url = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(
            subject || "",
        )}&body=${encodeURIComponent(body || "")}`;
        // Use an anchor's native click behavior so the OS mailto handler fires
        // reliably across browsers. window.open() can be flaky for mailto.
        try {
            const a = document.createElement("a");
            a.href = url;
            a.rel = "noopener";
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch {
            setShowFallback(true);
        }
    };

    const handleCopyFallback = async () => {
        try {
            await navigator.clipboard.writeText(copyText);
            toast.success("Copied subject + body to clipboard");
        } catch {
            toast.error("Copy failed — select the text below and copy manually");
        }
    };

    return (
        <div className="flex items-center gap-2">
            <button
                onClick={handleClick}
                disabled={!hasEmail}
                title={hasEmail ? `Open mailto: ${email}` : "No email available"}
                data-testid={`approval-compose-mailto-${prospectId}`}
                className="flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.15em] px-3 py-2 border border-[#0a84ff]/40 text-[#0a84ff] hover:bg-[#0a84ff]/10 disabled:opacity-40 disabled:cursor-not-allowed rounded-[2px]"
            >
                <Mail className="w-3 h-3" />
                Compose in mail app
            </button>
            {!hasEmail && (
                <span
                    className="font-mono text-[10px] text-[#6b7280]"
                    data-testid={`approval-compose-mailto-noemail-${prospectId}`}
                >
                    No email available
                </span>
            )}
            {hasEmail && (
                <button
                    onClick={() => setShowFallback((s) => !s)}
                    title="If your mail app didn't open, copy the text manually"
                    data-testid={`approval-compose-mailto-fallback-toggle-${prospectId}`}
                    className="font-mono text-[10px] text-[#9ca3af] hover:text-[#f3f4f6] underline-offset-2 hover:underline"
                >
                    {showFallback ? "hide copy" : "or copy"}
                </button>
            )}
            {showFallback && hasEmail && (
                <button
                    onClick={handleCopyFallback}
                    data-testid={`approval-compose-mailto-fallback-${prospectId}`}
                    className="ml-2 flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-1 border border-[#262a2e] text-[#9ca3af] hover:text-[#f3f4f6] hover:border-[#0a84ff] rounded-[2px]"
                >
                    <Copy className="w-3 h-3" />
                    Copy subject + body
                </button>
            )}
        </div>
    );
}
